import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Search } from 'lucide-react';
import { fetchPeople } from '@/lib/people-data';
import { SEARCH_LIMIT, SEARCH_MIN_CHARS, searchFacts, searchTitle } from '@/lib/member-search';
import s from '@/pages/console/console.module.css';

/**
 * Find a member by name or email from any page. Results open the person
 * page (/people/:id). Enter opens the first match; Escape closes the list.
 */
export function MemberSearch() {
  const [q, setQ] = useState('');
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const listId = useId();
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  // Both the settled term and what is in the box now: clearing the box closes the list at once, not after the debounce.
  const enabled = term.length >= SEARCH_MIN_CHARS && q.trim().length >= SEARCH_MIN_CHARS;
  const res = useQuery({
    queryKey: ['member-search', term],
    queryFn: () => fetchPeople({ q: term, pageSize: SEARCH_LIMIT, includeQa: true }),
    enabled,
    staleTime: 30_000,
  });
  const rows = enabled ? res.data?.people ?? [] : [];

  const go = (id: string) => {
    setOpen(false);
    setQ('');
    navigate(`/people/${encodeURIComponent(id)}`);
  };

  return (
    <div className={s.memberSearch} ref={box}>
      <Search size={14} aria-hidden="true" className={s.memberSearchIcon} />
      <input
        type="search"
        className={s.memberSearchInput}
        placeholder="Find a member: name or email"
        aria-label="Find a member by name or email"
        aria-controls={listId}
        aria-expanded={open && enabled}
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(e.target.value.trim().length > 0); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
          if (e.key === 'Enter' && rows[0]) go(rows[0].userId);
        }}
      />
      {open && enabled && (
        <div className={s.memberSearchList} id={listId} role="listbox" data-panel="member-search">
          {res.isLoading && <div className={s.empty}>Searching…</div>}
          {res.isError && <div className={s.empty}>The search did not answer. Try again.</div>}
          {!res.isLoading && !res.isError && rows.length === 0 && <div className={s.empty}>Nobody matches “{term}”.</div>}
          {rows.map((p) => {
            const f = searchFacts(p);
            return (
              <button type="button" role="option" aria-selected="false" key={p.userId} className={s.memberSearchRow} onClick={() => go(p.userId)} data-result={p.userId}>
                <span className={s.memberSearchName}>{searchTitle(p)}{p.name && p.name.trim() ? <span className={s.muted}> · {p.email}</span> : null}{p.qa ? <span className={s.muted}> · team/QA</span> : null}</span>
                <span className={s.memberSearchFacts}>
                  <span data-fact="status">{f.status}</span> · <span data-fact="plan">{f.plan}</span> · <span data-fact="last-check">{f.lastCheck}</span> · <span data-fact="platform">{f.platform}</span> · <span data-fact="help">{f.help}</span>
                </span>
              </button>
            );
          })}
          {res.data && res.data.total > rows.length && <div className={s.empty}>{res.data.total - rows.length} more; type more of the name or email.</div>}
        </div>
      )}
    </div>
  );
}
